import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import AOS from "aos";

import StaffModuleGate from "../Staff/components/StaffModuleGate";
import Loader from "../util/Loader/Loader";
import PageVisitLoader from "../Components/PageVisitLoader/PageVisitLoader";
import { AppToastHost } from "../util/appToast";
import "./scrollbar.css";

const Dashboard = lazy(() => import("../Components/Dashboard/Dashboard"));
const AboutUsPage = lazy(() => import("../Components/AboutUsPage/AboutUsPage"));
const FaqPage = lazy(() => import("../Components/FaqPage/FaqPage"));
const CaseStudy = lazy(() => import("../Components/CaseStudy/Casestudy"));

const ContactUsPage = lazy(() =>
  import("../Components/ContactUsPage/ContactUsPage")
);
const Host = lazy(() => import("../ExtraPages/Host"));
const Overstay = lazy(() => import("../ExtraPages/Overstay"));
const Privacy = lazy(() => import("../ExtraPages/Privacy"));

const Innovations = lazy(() => import("../Components/Innovations/Innovations"));
const SmartCity = lazy(() => import("../Components/SmartCity/SmartCity"));

const Booking = lazy(() => import("../Components/Booking/Booking"));
const EventsLandingPage = lazy(() =>
  import("../Events/pages/EventsLandingPage")
);
const EventDetailPage = lazy(() => import("../Events/pages/EventDetailPage"));
const EventFeedbackPage = lazy(() => import("../Events/pages/EventFeedbackPage"));
const EventOnboardingPage = lazy(() => import("../Events/pages/EventOnboardingPage"));
const EventBookingPlaceholder = lazy(() =>
  import("../Events/pages/EventBookingPlaceholder")
);
const ProjectsPage = lazy(() => import("../Events/pages/ProjectsPage"));
const VVIPExemptionScanPage = lazy(() =>
  import("../Events/pages/VVIPExemptionScanPage")
);
const VVIPExemptionListPage = lazy(() =>
  import("../Events/pages/VVIPExemptionListPage")
);
const VVIPVehiclesPage = lazy(() =>
  import("../Events/pages/VVIPVehiclesPage")
);
const EventBookingDetailsPage = lazy(() =>
  import("../Events/pages/EventBookingDetailsPage")
);
const EventCertificatePage = lazy(() =>
  import("../Events/pages/EventCertificatePage")
);
const StaffPrebookPage = lazy(() => import("../Events/pages/StaffPrebookPage"));
const EventTermsStandalonePage = lazy(() =>
  import("../Events/pages/EventTermsStandalonePage")
);
const ValetLookupPage = lazy(() => import("../Events/pages/ValetLookupPage"));
const CartLookupPage = lazy(() => import("../Events/pages/CartLookupPage"));
const VendorOrdersLookupPage = lazy(() =>
  import("../Events/pages/VendorOrdersLookupPage")
);
const StallOrderPage = lazy(() => import("../Events/pages/StallOrderPage"));
const WalkinBookingPage = lazy(() =>
  import("../Events/pages/WalkinBookingPage")
);
const AccreditationFormPage = lazy(() =>
  import("../Events/pages/AccreditationFormPage")
);
const SponsorFormPage = lazy(() =>
  import("../Events/pages/SponsorFormPage")
);
const StaffSponsorsPage = lazy(() =>
  import("../Staff/pages/StaffSponsorsPage")
);
const AccreditationLookupPage = lazy(() =>
  import("../Events/pages/AccreditationLookupPage")
);
const AccreditationListPage = lazy(() =>
  import("../Staff/pages/accreditation/AccreditationListPage")
);
const PayLinkLanding = lazy(() => import("../Events/pages/PayLinkLanding"));
const ReceiptPage = lazy(() => import("../Events/pages/ReceiptPage"));
// const OurSolutions = lazy(() =>
//   import("../Components/OurSolutions/OurSolutions")
// );

// Web staff ops console — mirror of the parkadda-b2b-app staff surface.
// Each module (Valet / Cart / VendorPay) is gated by the same role
// capabilities used in the Flutter app.
const StaffLoginPage = lazy(() => import("../Staff/pages/StaffLoginPage"));
const StaffHomePage = lazy(() => import("../Staff/pages/StaffHomePage"));
const StaffProfilePage = lazy(() => import("../Staff/pages/StaffProfilePage"));
const StaffBookingsPage = lazy(() => import("../Staff/pages/StaffBookingsPage"));
const StaffScanPage = lazy(() => import("../Staff/pages/StaffScanPage"));
const StaffGameStatusPage = lazy(() => import("../Staff/pages/StaffGameStatusPage"));
const StaffDashboardPage = lazy(() => import("../Staff/pages/StaffDashboardPage"));
const StaffCommitteesPage = lazy(() => import("../Staff/pages/StaffCommitteesPage"));
const StaffAccountsPage = lazy(() => import("../Staff/pages/StaffAccountsPage"));
const StaffSiteFeedbackPage = lazy(() => import("../Staff/pages/StaffSiteFeedbackPage"));
const ValetRoutes = lazy(() => import("../Staff/pages/valet/ValetRoutes"));
const CartRoutes = lazy(() => import("../Staff/pages/cart/CartRoutes"));
const VpRoutes = lazy(() => import("../Staff/pages/vendorpay/VpRoutes"));
const VendorPortalRoutes = lazy(() => import("../VendorPortal/VendorPortalRoutes"));
const SupplyRoutes = lazy(() => import("../Supply/SupplyRoutes"));

// Web trooper POS — mirror of parkadda-b2b-app Trooper module.
const TrooperLoginPage = lazy(() => import("../Trooper/pages/TrooperLoginPage"));
const TrooperRoutes = lazy(() => import("../Trooper/pages/TrooperRoutes"));
// Public marketing page for the Trooper Smart POS product.
const TrooperLandingPage = lazy(() =>
  import("../Trooper/marketing/TrooperLandingPage")
);
const CitizenRoutes = lazy(() => import("../Citizen/CitizenRoutes"));

const NotFound = lazy(() => import("../util/NotFound/NotFound"));

/** Match React Router to CRA ``PUBLIC_URL`` when the app is built for a subpath. */
function publicUrlBasename() {
  var raw = process.env.PUBLIC_URL || "";
  if (!raw || raw === "/") return undefined;
  return raw.replace(/\/+$/, "") || undefined;
}

export const Routes = () => {
  useEffect(function () {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
      offset: 60,
      disable: function () {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      }
    });
  }, []);

  return (
    <BrowserRouter basename={publicUrlBasename()}>
      <AppToastHost />
      <PageVisitLoader />
      <Suspense fallback={<Loader />}>
        <Switch>
          <Route exact path="/" component={Dashboard} />
          <Route exact path="/booking" component={Booking} />
          <Route exact path="/events" component={EventsLandingPage} />
          <Route exact path="/events/:eventSlug/sponsor" component={SponsorFormPage} />
          <Route exact path="/events/feedback" component={EventFeedbackPage} />
          <Route exact path="/events/onboarding" component={EventOnboardingPage} />
          <Route exact path="/events/view" component={EventDetailPage} />
          <Route exact path="/events/book" component={EventBookingPlaceholder} />
          <Route exact path="/events/booking" component={EventBookingDetailsPage} />
          <Route exact path="/events/certificate/:bookingId" component={EventCertificatePage} />
          <Route exact path="/events/terms-and-conditions" component={EventTermsStandalonePage} />
          <Redirect exact from="/events/staff-prebook" to="/staff/cash" />
          {/* Standalone customer service modules (outside the events flow) */}
          <Route exact path="/valet" component={ValetLookupPage} />
          <Route exact path="/cart" component={CartLookupPage} />
          <Route exact path="/vendor-orders" component={VendorOrdersLookupPage} />
          <Route exact path="/events/stall-order" component={StallOrderPage} />
          {/* Short, brandable stall URL: /stall/<public_slug>.  Same page —
              it reads the slug and resolves the storefront server-side. */}
          <Route exact path="/stall/:slug" component={StallOrderPage} />
          {/* Public standalone walk-in booking — a guest arriving at an
              event without a pre-booking can pay for cart/valet access
              online and be scanned at the POS. */}
          <Route exact path="/walkin/:service" component={WalkinBookingPage} />

          {/* Public accreditation: apply + self-service lookup.
              Edit is scoped to the applicant's own mobile number. */}
          <Route exact path="/accreditation/:eventId" component={AccreditationFormPage} />
          <Route exact path="/accreditation/:eventId/lookup" component={AccreditationLookupPage} />

          {/* Unified SMS pay-link landing + receipt surface.  One code
              path for event bookings, valet walk-ins, cart walk-ins,
              and vendor orders — backend routes by ``:kind``. */}
          <Route exact path="/pay/:kind/:ref" component={PayLinkLanding} />
          <Route exact path="/receipt/:kind/:ref" component={ReceiptPage} />
          {/* Event stall vendor portal (VendorPay only — not HW/SW supply) */}
          <Route path="/vendor-portal" component={VendorPortalRoutes} />
          {/* Hardware / software supply (internal B2B — not events) */}
          <Route path="/supply" component={SupplyRoutes} />

          {/* Public product landing for the Trooper Smart POS.  Kept on a
              dedicated URL so the private /trooper/* operator app is not
              disturbed. */}
          <Route exact path="/trooper-pos" component={TrooperLandingPage} />

          {/* Parking trooper POS — web mirror of parkadda-b2b-app trooper login */}
          <Route exact path="/trooper/login" component={TrooperLoginPage} />
          <Route path="/trooper" component={TrooperRoutes} />

          {/* Citizen B2C — parking discovery, prepaid booking, host module */}
          <Route path="/citizen" component={CitizenRoutes} />
          <Route
            exact
            path="/supply/e-invoice/:ref"
            render={function (props) {
              var ref = props.match.params.ref;
              var api = (process.env.REACT_APP_API_ROOT || "https://server.parkadda.com").replace(/\/+$/, "");
              window.location.href = api + "/api/vendor/supply/e-invoice/" + encodeURIComponent(ref) + "/";
              return null;
            }}
          />
          {/* Public e-invoice alias → API HTML (SMS links use /e-invoice/vendor/REF) */}
          <Route
            exact
            path="/e-invoice/vendor/:ref"
            render={function (props) {
              var ref = props.match.params.ref;
              var api = (process.env.REACT_APP_API_ROOT || "https://server.parkadda.com").replace(/\/+$/, "");
              window.location.href = api + "/api/vendorpay/e-invoice/" + encodeURIComponent(ref) + "/";
              return null;
            }}
          />

          {/* Staff ops console — web mirror of the B2B app.
              Roles are enforced by StaffLayout + the backend. */}
          <Route exact path="/staff/login" component={StaffLoginPage} />
          <Route exact path="/staff" component={StaffHomePage} />
          <Route exact path="/staff/profile" component={StaffProfilePage} />
          <Route exact path="/staff/cash" component={StaffPrebookPage} />
          <Route exact path="/staff/dashboard" component={StaffDashboardPage} />
          <Route exact path="/staff/bookings" component={StaffBookingsPage} />
          <Route exact path="/staff/game-status" component={StaffGameStatusPage} />
          <Route exact path="/staff/scan" component={StaffScanPage} />
          <Route exact path="/staff/sponsors" component={StaffSponsorsPage} />
          <Route
            exact
            path="/staff/committees"
            render={function () {
              return (
                <StaffModuleGate require="admin">
                  <StaffCommitteesPage />
                </StaffModuleGate>
              );
            }}
          />
          <Route
            exact
            path="/staff/accounts"
            render={function () {
              return (
                <StaffModuleGate require="accounts">
                  <StaffAccountsPage />
                </StaffModuleGate>
              );
            }}
          />
          <Route
            exact
            path="/staff/feedback"
            render={function () {
              return (
                <StaffModuleGate require="feedback">
                  <StaffSiteFeedbackPage />
                </StaffModuleGate>
              );
            }}
          />
          <Route
            path="/staff/valet"
            render={function () {
              return (
                <StaffModuleGate require="valet">
                  <ValetRoutes />
                </StaffModuleGate>
              );
            }}
          />
          <Route
            path="/staff/cart"
            render={function () {
              return (
                <StaffModuleGate require="cart">
                  <CartRoutes />
                </StaffModuleGate>
              );
            }}
          />
          <Route
            path="/staff/vendorpay"
            render={function () {
              return (
                <StaffModuleGate require="vendorpay">
                  <VpRoutes />
                </StaffModuleGate>
              );
            }}
          />
          <Route
            exact
            path="/staff/accreditation"
            render={function () {
              return (
                <StaffModuleGate require="accreditation">
                  <AccreditationListPage />
                </StaffModuleGate>
              );
            }}
          />

          <Route exact path="/projects" component={ProjectsPage} />
          <Route exact path="/vvip_exemption" component={VVIPExemptionScanPage} />
          <Route exact path="/vvip-exemptions" component={VVIPExemptionListPage} />
          <Route exact path="/vvip-vehicles" component={VVIPVehiclesPage} />

          <Route exact path="/aboutUs" component={AboutUsPage} />
          <Route exact path="/faq" component={FaqPage} />
          <Route exact path="/caseStudy" component={CaseStudy} />
          <Route exact path="/contactUs" component={ContactUsPage} />
          {/* <Route exact path="/appstream" component={AppStreamCam} /> */}
          <Route exact path="/host" component={Host} />
          <Route exact path="/overstay" component={Overstay} />
          <Route exact path="/privacy" component={Privacy} />

          <Route exact path="/ourInnovations" component={Innovations} />
          <Route exact path="/smartCity" component={SmartCity} />
          {/* <Route exact path="/ourSolutions" component={OurSolutions} /> */}

          <Route exact path="*" component={NotFound} />
        </Switch>
      </Suspense>
    </BrowserRouter>
  );
};

// black = #1D2B44
// yellow = #FFC738
